import { JobFunction } from '@atlas/types'
import { jobFunctionLabel } from './job-functions'

/**
 * Picker ordering helpers.
 *
 * A dropdown whose options arrive in API/insertion order is effectively
 * unordered to the person reading it — finding a known entry means scanning the
 * whole list. Every picker over *data* (entities, registry values) should be
 * ordered; the exceptions are lists whose order carries meaning — workflow
 * statuses by `position`, roles by ascending privilege, severities by rank —
 * which must keep their own order and never use these helpers.
 *
 * Axis values are ordered server-side (hierarchy first, then alphabetically
 * among siblings) so a child stays contiguous with its parent; sorting those
 * again here would flatten the tree back into a scrambled list.
 */

/** Locale-aware, case-insensitive label comparison; digits compare numerically ("L2" before "L10"). */
export const byLabelAsc = <T extends { label: string }>(a: T, b: T): number =>
	a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })

/** Alphabetically ordered copy of a flat option list — never mutates the input. */
export const sortedByLabel = <T extends { label: string }>(options: readonly T[]): T[] => [...options].sort(byLabelAsc)

/** The minimum user shape a person-picker option needs. */
interface PickableUser {
	userId: string
	displayName: string
	email: string
	jobFunction?: string
}

/** A person's job function as shown beside their name, or null when they have none stated. */
export const jobFunctionOf = (user: Pick<PickableUser, 'jobFunction'>): string | null =>
	user.jobFunction && user.jobFunction !== JobFunction.UNASSIGNED ? jobFunctionLabel(user.jobFunction) : null

/**
 * People-picker options, by display name. Shared so every place that assigns
 * work — the form's Assignee and PM fields, the queue card — offers the same
 * list in the same order.
 *
 * The job function rides in the description, which is both shown under the name
 * and searched by the combobox filter: typing "designer" narrows the list to
 * designers without needing a separate control. `UNASSIGNED` is omitted rather
 * than printed — "Unassigned" beside a person reads as though the row itself is
 * unassigned.
 */
export const userOptions = (users: readonly PickableUser[] | undefined) =>
	sortedByLabel(
		(users ?? []).map((u) => {
			const jobFunction = jobFunctionOf(u)
			return {
				value: u.userId,
				label: u.displayName,
				description: jobFunction ? `${jobFunction} · ${u.email}` : u.email,
			}
		}),
	)

/**
 * The people who do `jobFunction`, plus whoever already holds the slot so the picker can
 * still show them. Every user is UNASSIGNED until someone states a job function, so when
 * nobody has it the list falls back to everyone: an empty picker is worse than an unfiltered one.
 */
export const withJobFunction = <T extends Pick<PickableUser, 'userId' | 'jobFunction'>>(
	users: readonly T[] | undefined,
	jobFunction: JobFunction,
	currentUserId?: string | null,
): T[] => {
	const all = users ?? []
	if (!all.some((u) => u.jobFunction === jobFunction)) return [...all]
	return all.filter((u) => u.jobFunction === jobFunction || u.userId === currentUserId)
}

/**
 * The users who may be made the order's PM, plus whoever already is (found in the directory)
 * so the picker can still show them. Unlike {@link withJobFunction} there is no fallback to
 * everyone: the server refuses anyone else as a new PM.
 */
export const activePms = <T extends { userId: string }>(
	pms: readonly T[] | undefined,
	users: readonly T[] | undefined,
	currentUserId?: string | null,
): T[] => {
	const list = [...(pms ?? [])]
	if (!currentUserId || list.some((user) => user.userId === currentUserId)) return list
	const current = users?.find((user) => user.userId === currentUserId)
	return current ? [...list, current] : list
}
